import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { CheckCircle2, Shield, ArrowRight } from 'lucide-react';

export const ClinicalQualityComplianceAnalytics = () => {
  const relatedServices = [
    { title: "IVF & Fertility Analytics", url: "/services/ivf-fertility-analytics", description: "Biostatistics-led fertility analytics for UAE IVF clinics" },
    { title: "Healthcare Performance Analytics", url: "/services/healthcare-performance-analytics", description: "Executive dashboards and performance analytics" },
    { title: "Predictive Analytics & Decision Intelligence", url: "/services/predictive-analytics-decision-intelligence", description: "Predictive modelling and machine learning" },
    { title: "Research & Biostatistics", url: "/services/research-biostatistics", description: "End-to-end biostatistics support" }
  ];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Clinical Quality & Compliance Analytics",
    "description": "Accreditation-ready quality and compliance analytics for UAE healthcare. DHA, DOH, MOH, and JCI reporting.",
    "provider": { "@type": "Organization", "name": "StatZen Analytics", "url": "https://statzenanalytics.com" },
    "areaServed": { "@type": "Place", "name": "United Arab Emirates" },
    "serviceType": "Healthcare Analytics"
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Clinical Quality & Compliance Analytics | StatZen UAE</title>
        <meta name="description" content="Accreditation-ready quality and compliance analytics for UAE healthcare. DHA, DOH, MOH, and JCI reporting, quality indicators, and audit dashboards." />
        <link rel="canonical" href="https://statzenanalytics.com/services/clinical-quality-compliance-analytics" />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-5xl mx-auto">
          <Link to="/services" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6 transition-colors">
            <ArrowRight className="w-4 h-4 mr-2 rotate-180" />Back to All Services
          </Link>
          <div className="flex items-start space-x-6 mb-8">
            <div className="w-20 h-20 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Shield className="w-10 h-10 text-blue-600" />
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">03</div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Clinical Quality & Compliance Analytics</h1>
              <p className="text-xl text-gray-700 italic">Simplify Accreditation Through Better Data.</p>
            </div>
          </div>
          <p className="text-lg text-gray-600 leading-relaxed">Healthcare regulations require more than documentation, they require measurable performance. We help organizations monitor quality indicators and stay prepared for accreditation.</p>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">We Help You:</h2>
              <ul className="space-y-3">
                {["DHA Analytics Support", "DOH & MOH Reporting", "JCI Performance Indicators", "Clinical Audit Analytics", "Quality Indicator Dashboards", "Accreditation Readiness Analytics", "Outcome Benchmarking", "Continuous Quality Improvement Reporting"].map((item, i) => (
                  <li key={i} className="flex items-start space-x-3"><CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" /><span className="text-gray-700">{item}</span></li>
                ))}
              </ul>
            </div>
            <div className="space-y-8">
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Ideal For:</h3>
                <p className="text-gray-700">Hospitals, IVF Clinics, Healthcare Networks, Quality Departments</p>
              </div>
              <div className="border-l-4 border-teal-500 pl-6 py-4 bg-teal-50 rounded-r-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Business Outcomes:</h3>
                <ul className="space-y-3">
                  {["Simplify compliance", "Improve quality indicators", "Reduce reporting effort", "Support accreditation readiness"].map((item, i) => (
                    <li key={i} className="flex items-start space-x-3"><CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" /><span className="text-gray-700">{item}</span></li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-8 text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Simplify Your Accreditation Process?</h2>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">Book a free consultation to discuss quality and compliance analytics for your organization.</p>
            <Link to="/contact"><button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">Book a Free Consultation</button></Link>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedServices.map((service, index) => (
                <Link key={index} to={service.url}>
                  <Card className="h-full hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{service.title}</h3>
                      <p className="text-sm text-gray-600">{service.description}</p>
                      <div className="mt-4 text-blue-600 text-sm font-semibold flex items-center">Learn More <ArrowRight className="w-4 h-4 ml-2" /></div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClinicalQualityComplianceAnalytics;
